/* eslint-disable @tanstack/query/exhaustive-deps */
import { useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import {
  CommercialEventAccessResponse,
  CommercialType,
  ExhibitorCommercialClientSecuredType,
  ExhibitorCommercialClientType,
  ExhibitorCommercialMeetingType,
  FilterClientTypeWithPaginationAndSorting,
  Paginated,
  PaginatedResponse,
  QueryArgs,
  ScannedClientByCommercial,
  Sortable,
  StructureType,
} from "@ac-expo/api-types";
import {
  commercialClientsQueryKeys,
  commercialMeetingsQueryKeys,
  commercialQueryKeys,
  commercialScanQueryKeys,
} from "../query-keys";

const getCommercialMeetings = async () => {
  const { data } = await privateAxios.get<ExhibitorCommercialMeetingType[]>(
    `exhibitor-commercial/meetings`
  );
  return data;
};

export const useGetCommercialMeetings = () => {
  return useQuery({
    queryKey: commercialMeetingsQueryKeys.all,
    queryFn: getCommercialMeetings,
  });
};

const getCommercialClientComment = async (
  clientd: string,
  structureId: string,
  eventId: string
) => {
  const { data } = await privateAxios.get<ExhibitorCommercialClientType>(
    `/exhibitor-commercial/${structureId}/${eventId}/client/${clientd}`
  );
  return data;
};

export const useGetCommercialClientComment = (
  clientd: string | undefined,
  structureId: string,
  eventId: string
) => {
  return useQuery({
    enabled: !!clientd,
    queryKey: commercialClientsQueryKeys.byId(clientd!),
    queryFn: () => getCommercialClientComment(clientd!, structureId, eventId),
  });
};

const getUserCommercial = async () => {
  const res = await privateAxios.get<CommercialType | null>(
    "/exhibitor-commercial/mine"
  );
  return res.data;
};

export const useGetUserCommercialQuery = () => {
  return useQuery({
    queryKey: commercialQueryKeys.mine(),
    queryFn: getUserCommercial,
  });
};

const getCommercialEventAccess = async (eventId: string) => {
  const { data } = await privateAxios.get<CommercialEventAccessResponse>(
    `/exhibitor-commercial/events/${eventId}/access`
  );
  return data;
};

export const useGetCommercialEventAccessQuery = <
  TData = CommercialEventAccessResponse,
>(
  eventId: string,
  options?: QueryArgs<CommercialEventAccessResponse, TData>
) => {
  return useQuery({
    enabled: !!eventId,
    queryKey: commercialQueryKeys.eventAccess(eventId),
    queryFn: () => getCommercialEventAccess(eventId),
    ...options,
  });
};

export const getCommercialStructureClients = async (
  eventId: string,
  structureId: string,
  filters?: FilterClientTypeWithPaginationAndSorting
) => {
  const { data } = await privateAxios.get<
    PaginatedResponse<ExhibitorCommercialClientSecuredType>
  >(`exhibitor-commercial/${structureId}/${eventId}/clients`, {
    params: filters,
  });
  return data;
};

export const useGetCommercialStructureClientsQuery = (
  filters: FilterClientTypeWithPaginationAndSorting,
  eventId: string,
  structureId?: string,
  options?: QueryArgs<PaginatedResponse<ExhibitorCommercialClientSecuredType>>
) => {
  return useQuery({
    enabled: !!structureId,
    queryKey: commercialClientsQueryKeys.byEvent(eventId, filters),
    queryFn: () => getCommercialStructureClients(eventId, structureId!),
    ...options,
  });
};

const getCommercialScanUserDetails = async (userId: string) => {
  const { data } = await privateAxios.get<ScannedClientByCommercial>(
    `/exhibitor-commercial/scan/details/user/${userId}`
  );
  return data;
};

export const useGetCommercialScanUserDetails = <
  TData = ScannedClientByCommercial,
>(
  userId?: string,
  options?: QueryArgs<ScannedClientByCommercial, TData>
) => {
  return useQuery({
    enabled: !!userId,
    queryKey: commercialScanQueryKeys.user(userId ?? ""),
    queryFn: () => getCommercialScanUserDetails(userId!),
    ...options,
  });
};

const getCommercialScanStructureDetails = async (structureId: string) => {
  const { data } = await privateAxios.get<StructureType>(
    `/exhibitor-commercial/scan/details/structure/${structureId}`
  );
  return data;
};

export const useGetCommercialScanStructureDetails = <TData = StructureType>(
  structureId?: string,
  options?: QueryArgs<StructureType, TData>
) => {
  return useQuery({
    enabled: !!structureId,
    queryKey: commercialScanQueryKeys.structure(structureId ?? ""),
    queryFn: () => getCommercialScanStructureDetails(structureId!),
    ...options,
  });
};
